'use strict';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import {buildInjector} from '../../_libs/angular-utils';
import {InPlaceEditor} from '../../core-utilities/domain/InPlaceEditorService';
import {AvatarColors} from '../../core-utilities/domain/AvatarColors';
import {
  createGeoJsonPoint,
  getLatLong,
  latLongToGeoJsonCoordinates,
  validateCoordinates,
} from '../../core-utilities/geo/utils';
import {logger} from './logger';


// load angular dependencies:
const dependencies = {};
export const injector = buildInjector(dependencies, [
  '$timeout',
  '$toast',
  'AssetDomain',
  'MapsService',
]);

export function AssetEditorFactory() {
  return AssetEditor;
}

export class AssetEditor extends InPlaceEditor {
  constructor($scope, options) {
    options.update = () => options.getModel().update();
    super(options);

    logger.debug('Geocoder Map Loading');
    dependencies.MapsService.mapLoaded()
      .then(mapApi => {
        logger.debug('Geocoder Map Loaded');
        this.getCoords = () => {
          logger.debug('Geocoder called getCoords');
          if (this.gettingCoords || (!this.coordsMatch && !this.coordsBlank())) return;
          const geocoder = new mapApi.Geocoder();
          $scope.$evalAsync(() => {
            this.gettingCoords = true;
          });
          geocoder.geocode({address: this.address}, (results, status) => {
            $scope.$evalAsync(() => {
              if (status == mapApi.GeocoderStatus.OK && results.length > 0) {
                const location = results[0].geometry.location;
                const lat = location.lat().toFixed(6);
                const lng = location.lng().toFixed(6);
                this.coords = [lat, lng];
                this.fetchResult = [lat, lng]; // stored for comparison
                this.gettingCoords = false;
              }
            });
          });
        };
      })
      .catch(err => {
        logger.error({err}, 'Geocoder Failed');
      });
  }

  edit() {
    const model = this.__config.getModel();

    if (model.geo) {
      if (model.geo.properties) {
        this.address = model.geo.properties.address || '';
        this.coordsMatch = !isUndefined(model.geo.properties.coordsMatch) ? model.geo.properties.coordsMatch : true;
      }

      const latLong = getLatLong(model.geo);
      if (latLong) {
        this.coords = [latLong.lat, latLong.lon];
      } else {
        this.coords = [];
      }
    } else {
      this.coordsMatch = true;
      this.address = '';
      this.coords = [];
    }

    this.initCoords = cloneDeep(this.coords);

    return super.edit(this);
  }

  save() {
    this.__working = true;
    return dependencies.$timeout(() => {
      const model = this.__config.getModel();
      let geo;
      // validate geo data
      const coords = AssetEditor.sanitizeCoordinates(this.coords);

      if (coords && !validateCoordinates(coords)) {
        // TODO: display errors using ng-messages per-input field
        // geo data will need custom error message handling since AngualrDomain does not well-support JSON property error messages
        // eslint-disable-next-line max-len
        dependencies.$toast.showMessage('Latitude must be between -90 and +90. Longitude must be between -180 and 180. Otherwise leave blank.', null, 'error');
        this.__working = false;
        return Promise.reject(model.$errors);
      }

      if (!isNumber(model.avc)) {
        model.avc = AvatarColors.getRand();
      }

      // validate model
      if (!model.$validate()) {
        console.warn(model.$errors);
        // TODO: display errors using ng-messages per-input field
        dependencies.$toast.showMessage('New assets must have a name or ID!', null, 'error');
        this.__working = false;
        return Promise.reject(model.$errors);
      }

      // write updated geo data to model if changed
      if (this.address !== '' || coords !== null) {
        if (model.geo) {
          geo = cloneDeep(model.geo);
        } else {
          geo = createGeoJsonPoint();
        }

        if (this.address !== '') {
          geo.properties.address = this.address;
        } else {
          delete geo.properties.address;
        }

        if (!geo.geometry)
          geo.geometry = {};

        if (coords) {
          geo.geometry.coordinates = latLongToGeoJsonCoordinates({
            lat: coords[0],
            lon: coords[1],
          });
          geo.properties.coordsMatch = this.coordsMatch;
        } else {
          geo.geometry.coordinates = null;
          geo.properties = omit(geo.properties, ['coordsMatch']);
        }

        if (Object.keys(geo.properties).length === 0) {
          delete geo.properties;
        }

        model.geo = geo;
      } else {
        model.geo = null;
      }

      return super.save(this);
    }, 100);
  }

  revert() {
    const model = this.__config.getModel();
    if (model) {
      model.$revert();

      if (model.geo) {
        this.address = model.geo.properties && model.geo.properties.address || '';
        const latLong = getLatLong(model.geo);
        this.coords = latLong ? [latLong.lat, latLong.lon] : [];
      }
    } else {
      this.address = '';
      this.coords = [];
    }

    super.revert(this);
  }

  coordsBlur() {
    this.coordsMatch = isEqual(this.fetchResult ? this.fetchResult : this.initCoords, this.coords);
  }

  updateCoords() {
    this.coordsMatch = true;
    this.getCoords();
  }

  coordsBlank() {
    if (!this.coords || this.coords.length === 0) return true;
    else return !this.coords[0] && !this.coords[1];
  }


  /**
   * Converts Array.<String> into Array.<Number> if needed
   * @param {Array} coordinates - Latitude, Longitude
   * @returns {Array.<Number>}
   */
  static sanitizeCoordinates(coordinates) {
    const result = [
      !isUndefined(coordinates[0]) ? AssetEditor._convertToNumber(coordinates[0]) : null,
      !isUndefined(coordinates[0]) ? AssetEditor._convertToNumber(coordinates[1]) : null,
    ];

    if (result[0] === null || result[1] === null) {
      return null;
    } else {
      return result;
    }
  }

  static _convertToNumber(value) {
    let result = null;
    if (isNumber(value)) {
      result = value;
    } else if (isString(value) && value !== '') {
      try {
        result = parseFloat(value);
      } catch (e) {
      }
    }
    return result;
  }
}



// WEBPACK FOOTER //
// ./src/js/assets/services/AssetEditorFactory.js